import {
    AfterViewInit,
    Component,
    computed,
    HostListener,
    input,
    ViewChildren,
    QueryList,
    ElementRef,
    Inject,
    PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { DirectivesModule } from '@jht/brand-directives';
import {
    ComponentsModule,
    ModalModule,
    ModalService,
} from '@jht/brand-components';
import { TranslateModule } from '@jht/brand-translate';
import { BuilderComponent, BuilderField } from '@jht/brand-page-builder/core';
import { VideoData, VideoService } from '../../services/video.service';

@Component({
    selector: 'vision2-side-by-side',
    imports: [
        CommonModule,
        ComponentsModule,
        DirectivesModule,
        ModalModule,
        TranslateModule,
    ],
    templateUrl: './side-by-side.component.html',
    styleUrl: './side-by-side.component.scss',
})
@BuilderComponent({
    label: 'Side By Side',
    category: 'Content',
    sectionClass: '',
})
export class SideBySideComponent implements AfterViewInit {
    buttonClass = input<string>('p-0');
    @BuilderField({ group: 'button' }) buttonText = input<string>();
    @BuilderField({ group: 'button' }) buttonUrl = input<string>();
    @BuilderField({
        type: 'color',
        options: [
            'text-bg-light',
            'text-bg-white',
            'text-bg-gray-10',
            'text-bg-gray-30',
            'text-bg-tertiary',
        ],
        default: 'text-bg-light',
        description: 'Background and text colour behind the whole block.',
    })
    colorClass = input<string>();
    @BuilderField({ type: 'color', options: ['bg-transparent', 'bg-gray-20'] })
    contentColor = input<string>('bg-transparent');
    centered = input<boolean>(true);
    containerType = input<string>('container');
    containerPadding = input<string>('');
    @BuilderField({ default: 'col-md-10 col-lg-6 col-xl-5' })
    contentClass = input<string>();
    contentPadding = input<string>();
    @BuilderField({
        type: 'richtext',
        help: 'Markdown. Separate paragraphs with a blank line.',
    })
    description = input<string>();
    heading = input<string>();
    @BuilderField({ description: 'Heading level rendered (h1-h5).' })
    headingValue = input<number>();
    headingClass = input<string>();
    image = input<string>();
    imageAlt = input<string>();
    @BuilderField({ default: 'col-lg-6' }) imageClass = input<string>();
    @BuilderField({ type: 'image' }) logo = input<string>('');
    logoAlt = input<string>();
    @BuilderField({ group: 'link' }) linkText = input<string>();
    @BuilderField({ group: 'link' }) linkUrl = input<string>();
    @BuilderField({
        label: 'Rounded corners',
        optionLabels: {
            'rounded-start-4': 'Left side',
            'rounded-end-4': 'Right side',
            'rounded-4': 'All corners',
        },
        description: 'Which corners of the image are rounded.',
    })
    roundedImage = input<'rounded-start-4' | 'rounded-end-4' | 'rounded-4'>(
        'rounded-4',
    );
    subheading = input<string>();
    @BuilderField({
        label: 'Image position',
        optionLabels: { left: 'Left', right: 'Right' },
        description:
            'Which side the image sits on at desktop widths. Smaller screens stack the image first.',
    })
    type = input<'left' | 'right'>('left');
    @BuilderField({
        group: 'video',
        help: 'Vimeo video id. Shows a button that opens the video in a modal.',
    })
    modalVideoId = input<string>();
    verticalPadding = input<string>('py-4 py-lg-3');
    @BuilderField({
        description:
            'Vimeo video id. Plays inline in the image column instead of a static image.',
    })
    inlineVideoId = input<string>();
    @BuilderField({
        type: 'order',
        label: 'Actions',
        help: 'Drag to change the order these render in.',
    })
    actionOrder = input<string[]>(['video', 'button', 'link']);

    inlineVideoUrl!: VideoData;
    modalVideoUrl!: VideoData;
    currentModal = '';
    inlineVideoLoaded: boolean = false;
    modalVideoLoaded: boolean = false;
    isBrowser = false;
    isHidden = false;

    constructor(
        private modalService: ModalService,
        private videoService: VideoService,
        @Inject(PLATFORM_ID) private platformId: Object,
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    readonly isExternalImage = computed(() => {
        const img = this.image();
        return (
            typeof img === 'string' &&
            img.includes('assets.johnsonhealthtech.com')
        );
    });

    @HostListener('document:keydown.escape', ['$event'])
    onEscPressed(_: Event) {
        this.closeModal(this.currentModal);
    }

    @ViewChildren('sideBySideImage')
    sideBySideImages!: QueryList<ElementRef>;
    @ViewChildren('sideBySideLogo')
    sideBySideLogos!: QueryList<ElementRef>;
    @ViewChildren('sideBySideHeading')
    sideBySideHeadings!: QueryList<ElementRef>;
    @ViewChildren('sideBySideDescription')
    sideBySideDescriptions!: QueryList<ElementRef>;
    @ViewChildren('sideBySideButton')
    sideBySideButtons!: QueryList<ElementRef>;
    @ViewChildren('sideBySideLink')
    sideBySideLinks!: QueryList<ElementRef>;
    @ViewChildren('sideBySideModalLink')
    sideBySideModalLinks!: QueryList<ElementRef>;

    ngAfterViewInit() {
        if (this.isBrowser) {
            if (this.inlineVideoId()) {
                this.initializeInlineVideos();
            }

            if (this.modalVideoId()) {
                this.initializeModalVideos();
            }
        }
    }

    private initializeInlineVideos() {
        const inlineVideoInput = this.inlineVideoId();
        const inlineVideoOptions = {
            autoplay: true,
            autopause: true,
            background: false,
            controls: true,
            loop: true,
            muted: true,
            responsive: false,
        };
        if (inlineVideoInput) {
            this.videoService
                .getVideoData(inlineVideoInput, { ...inlineVideoOptions })
                .then((data: any) => {
                    if (data) {
                        this.inlineVideoUrl = data;
                    }
                    this.inlineVideoLoaded = true;
                });
        }
    }

    private initializeModalVideos() {
        const modalVideoInput = this.modalVideoId();
        const modalVideoOptions = {
            autoplay: true,
            autopause: true,
            background: false,
            controls: true,
            loop: false,
            muted: false,
            responsive: false,
        };
        if (modalVideoInput) {
            this.videoService
                .getVideoData(modalVideoInput, { ...modalVideoOptions })
                .then((data: any) => {
                    if (data) {
                        this.modalVideoUrl = data;
                    }
                });
        }
    }

    openModal(id: string): void {
        if (id) {
            this.currentModal = id;
            this.modalVideoLoaded = true;
            this.modalService.open(id);
        }
    }

    closeModal(id: string): void {
        if (id) {
            this.currentModal = '';
            this.modalVideoLoaded = false;
            this.modalService.close(id);
        }
    }
}
